import { z } from 'zod';

// ===== User =====
export const UserSchema = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string().email(),
  displayName: z.string().optional(),
  avatar_url: z.string().optional().nullable(),
  emailVerified: z.boolean().optional(),
  createdAt: z.string().optional(),
  lastSignIn: z.string().optional(),
});
export type UserDto = z.infer<typeof UserSchema>;

// ===== Tokens =====
export const TokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  accessTokenExpires: z.string().optional(),
  refreshTokenExpires: z.string().optional(),
});
export type TokensDto = z.infer<typeof TokensSchema>;

// ===== Base API response envelope =====
export const BaseSuccessSchema = z.object({
  success: z.literal(true),
  message: z.string().optional(),
  requestId: z.string().optional(),
});

// ===== Sign Up =====
export const SignUpRequestSchema = z.object({
  username: z.string().min(3).max(20),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  displayName: z.string().optional(),
});
export type SignUpRequest = z.infer<typeof SignUpRequestSchema>;

export const SignUpResponseSchema = BaseSuccessSchema.extend({
  data: z.object({
    user: UserSchema.pick({ id: true, username: true, email: true, emailVerified: true, avatar_url: true }),
    tokens: TokensSchema,
  }),
});
export type SignUpResponse = z.infer<typeof SignUpResponseSchema>;

// ===== Sign In =====
export const SignInRequestSchema = z.object({
  identifier: z.string().min(1), // email or username
  password: z.string().min(8).max(128),
});
export type SignInRequest = z.infer<typeof SignInRequestSchema>;

export const SignInResponseSchema = BaseSuccessSchema.extend({
  data: z.object({
    user: UserSchema.pick({ id: true, username: true, email: true, emailVerified: true, avatar_url: true }),
    tokens: TokensSchema,
  }),
});
export type SignInResponse = z.infer<typeof SignInResponseSchema>;

// ===== Verify Email =====
export const VerifyEmailRequestSchema = z.object({ token: z.string().min(1) });
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;

export const VerifyEmailResponseSchema = BaseSuccessSchema;
export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;

// ===== Forgot Password =====
export const ForgotPasswordRequestSchema = z.object({ email: z.string().email() });
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ForgotPasswordResponseSchema = BaseSuccessSchema;
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponseSchema>;

// ===== Reset Password =====
export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  newPassword: z.string().min(8).max(128),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

export const ResetPasswordResponseSchema = BaseSuccessSchema;
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;

// ===== Refresh Token =====
export const RefreshTokenRequestSchema = z.object({ refreshToken: z.string() });
export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

export const RefreshTokenResponseSchema = BaseSuccessSchema.extend({
  data: z.object({
    accessToken: z.string(),
    accessTokenExpires: z.string().optional(),
  }),
});
export type RefreshTokenResponse = z.infer<typeof RefreshTokenResponseSchema>;

// ===== Sign Out =====
export const SignOutResponseSchema = BaseSuccessSchema;
export type SignOutResponse = z.infer<typeof SignOutResponseSchema>;

// ===== Me =====
export const MeResponseSchema = BaseSuccessSchema.extend({
  data: z.object({ user: UserSchema.extend({ displayName: z.string().optional() }) }),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ===== Set Password (for OAuth users) =====
export const SetPasswordRequestSchema = z.object({
  new_password: z.string().min(8).max(128),
});
export type SetPasswordRequest = z.infer<typeof SetPasswordRequestSchema>;

export const SetPasswordResponseSchema = BaseSuccessSchema;
export type SetPasswordResponse = z.infer<typeof SetPasswordResponseSchema>;

// ===== OAuth Providers =====
export const OAuthProviderSchema = z.object({
  provider: z.string(),
  provider_username: z.string().nullable(),
  provider_email: z.string().nullable(),
  created_at: z.string(),
});
export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;

export const AuthenticationInfoSchema = z.object({
  hasPassword: z.boolean(),
  emailVerified: z.boolean(),
  canUnlinkProviders: z.boolean(),
});
export type AuthenticationInfo = z.infer<typeof AuthenticationInfoSchema>;

export const GetProvidersResponseSchema = BaseSuccessSchema.extend({
  data: z.object({
    providers: z.array(OAuthProviderSchema),
    authentication: AuthenticationInfoSchema,
  }),
});
export type GetProvidersResponse = z.infer<typeof GetProvidersResponseSchema>;

export const UnlinkProviderResponseSchema = BaseSuccessSchema;
export type UnlinkProviderResponse = z.infer<typeof UnlinkProviderResponseSchema>;

// ===== OAuth Callback =====
export const OAuthCallbackTokensSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  mode: z.enum(['sign_in_up', 'link_account']).optional(),
});
export type OAuthCallbackTokens = z.infer<typeof OAuthCallbackTokensSchema>;

// ===== Check Username Availability =====
export const CheckUsernameResponseSchema = z.object({
  available: z.boolean(),
  suggestions: z.array(z.string()).optional(),
  error: z.string().optional(),
});
export type CheckUsernameResponse = z.infer<typeof CheckUsernameResponseSchema>;

// ===== Complete OAuth Registration =====
export const CompleteOAuthRegistrationRequestSchema = z.object({
  registration_token: z.string().min(1),
  username: z.string().min(3).max(20),
});
export type CompleteOAuthRegistrationRequest = z.infer<typeof CompleteOAuthRegistrationRequestSchema>;

export const CompleteOAuthRegistrationResponseSchema = BaseSuccessSchema.extend({
  user: UserSchema.pick({ id: true, username: true, email: true, emailVerified: true }),
  tokens: TokensSchema,
});
export type CompleteOAuthRegistrationResponse = z.infer<typeof CompleteOAuthRegistrationResponseSchema>;

